
// src/components/MainWorkspace.tsx
import React, { useEffect } from 'react';
import {
    ProjectState, MainView
} from '../types.js';
import { useAppContext } from '../contexts/AppContext.js';
import { useSearchParams } from 'react-router-dom';
import { primaryButtonClass } from '../constants.js'; 

import IdeaHubView from './IdeaHubView.js';
import WorldBuildingView from './WorldBuildingView.js';
import CharactersView from './CharactersView.js';
import ChapterListPanel from './ChapterListPanel.js';
import ChapterDetailTabs from './ChapterDetailTabs.js';
import BookMetadataEditor from './BookMetadataEditor.js';
import BookStructureStudioView from './BookStructureStudioView.js'; 
import PublishingEssentialsEditor from './PublishingEssentialsEditor.js';
import BookPreview from './BookPreview.js';
import AIToolsView from './AIToolsView.tsx';
import { BookOpen } from 'lucide-react';

const PlaceholderMessage: React.FC<{viewName: string; customMessage?: string}> = ({viewName, customMessage}) => (
    <div className="flex-grow flex items-center justify-center text-gray-500 h-full">
        <div className="text-center p-4 sm:p-8 bg-white rounded-lg shadow-md border">
             <BookOpen className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground mx-auto mb-3 sm:mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-2">{viewName} is Locked</h3>
            <p className="max-w-sm mx-auto text-sm sm:text-base">
                {customMessage || `Please generate your Initial Blueprint from the "Project Idea" tab first to access ${viewName}.`}
            </p>
        </div>
    </div>
);

const MainWorkspace: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    
    const appContext = useAppContext();

    if (!appContext) return <p>Loading context...</p>;

    // Removed excessive console logging to prevent input lag

    const {
        projectState,
        creditBalance,
        requiredCredits,
        isLoading,
        isInitialBlueprintReady,
        mainView,
        activeChapter,
        editor,
        chapterManagement,
        chapterAIActions,
        fileAndCoverActions,
        projectSetupLogic,
        handleProjectStateFieldChange,
        handleTargetWordCountChange,
        handleTargetPageCountChange,
        handleBlueprintChange,
        handleAuthorDetailsChange,
        handleFrontMatterChange,
        handleBackMatterChange,
        onAddBackMatterListItem,
        onDeleteBackMatterListItem,
        onUpdateBackMatterListItem,
        handleBookMetadataChange,
        handlePublishingEssentialsChange,
        chapterState,
        modalStates,
        revisionInput,
        revisionWordCount,
        setRevisionInput,
        setRevisionWordCount,
        chapterPreviewPercentage,
        onChapterPreviewPercentageChange,
        haveDetailsOrWorldChangedSinceChapterGen,
        handleSelectChapter,
    } = appContext;
    
    if (!projectState) {
        return <div className="flex-grow flex items-center justify-center"><p>Loading project...</p></div>;
    }

    const showRevisionNeededBanner = projectState.chapters?.length > 0 && haveDetailsOrWorldChangedSinceChapterGen && haveDetailsOrWorldChangedSinceChapterGen();
    // Removed excessive console logging to prevent input lag
    const ideaHubProjectState = React.useMemo(() => ({
        outlineIdea: projectState.outlineIdea,
        workType: (projectState.bookMetadata as any)?.literaryForm || appContext.dbProject?.bookType || 'NOVEL',
        targetPageCount: projectState.targetPageCount,
        targetWordCount: projectState.targetWordCount,
        bookMetadata: projectState.bookMetadata,
        hasInitialBlueprint: projectState.hasInitialBlueprint,
        blueprint: projectState.blueprint,
    }), [
        projectState.outlineIdea,
        projectState.bookMetadata,
        appContext.dbProject?.bookType,
        projectState.targetPageCount,
        projectState.targetWordCount,
        projectState.hasInitialBlueprint,
        projectState.blueprint,
    ]);

    return (
        <div className="flex flex-1 overflow-hidden h-full">
            <main className="flex-1 p-2 sm:p-4 bg-gray-100 h-full overflow-y-auto">
                {mainView === 'ideaHub' && (
                    <div className={`flex-1 flex ${!isInitialBlueprintReady ? 'items-center justify-center h-full' : 'overflow-y-auto'}`}>
                         <IdeaHubView
                            projectState={ideaHubProjectState}
                            onOutlineIdeaChange={(val: string) => handleProjectStateFieldChange('outlineIdea', val)}
                            onWorkTypeChange={(val: string) => handleBookMetadataChange('genreSubject' as any, val)}
                            onTargetWordCountChange={handleTargetWordCountChange}
                            onTargetPageCountChange={handleTargetPageCountChange}
                            onStoryTitleChange={(title: string) => {
                                // BLUEPRINT MIGRATION: Use blueprint change handler
                                appContext.handleBlueprintChange('worldBuildingSuggestions', 'selectedStoryTitle', title);
                                handleBookMetadataChange('title', title);
                            }}
                            onGenerateInitialBlueprint={projectSetupLogic.handleGenerateInitialBlueprint} 
                            onRegenerateBlueprint={projectSetupLogic.handleGenerateInitialBlueprint}
                            onGenerateWithModel={(modelId: string) => {
                                // Model selection handler for when generation fails
                                projectSetupLogic.handleGenerateInitialBlueprint();
                            }}
                            isLoading={!!isLoading['initialBlueprintGen']} 
                            creditBalance={creditBalance}
                            requiredCredits={requiredCredits}
                            lastError={appContext.error ?? undefined}
                            failedModel={appContext.failedModel ?? undefined}
                        />
                    </div>
                )}

                {mainView === 'bookDetails' && (
                    <>
                        {isInitialBlueprintReady ? (
                            <div className="w-full bg-white p-3 sm:p-6 rounded-lg shadow-xl flex-grow h-full" style={{backgroundImage: "url('/assets/texture_tile.png')", backgroundRepeat: 'repeat', backgroundSize: 'auto'}}>
                                <BookMetadataEditor
                                    bookMetadata={projectState.bookMetadata}
                                    onBookMetadataChange={handleBookMetadataChange}
                                    hasInitialBlueprint={isInitialBlueprintReady}
                                />
                            </div>
                        ) : <PlaceholderMessage viewName="Book Details" />}
                    </>
                )}

                {mainView === 'worldBuilding' && (
                    isInitialBlueprintReady ? (
                        <WorldBuildingView
                            // BLUEPRINT MIGRATION: Pass blueprint data instead of legacy worldBuildingData
                            blueprint={projectState.blueprint}
                            onBlueprintChange={appContext.handleBlueprintChange}
                            hasInitialBlueprint={isInitialBlueprintReady}
                            outlineIdea={projectState.outlineIdea}
                            bookMetadata={projectState.bookMetadata}
                            coreConcepts={{
                                logLine: projectState.blueprint?.worldBuildingSuggestions?.logLine || '',
                                storyStartConcept: projectState.blueprint?.worldBuildingSuggestions?.storyStartConcept || '',
                                storyEndConcept: projectState.blueprint?.worldBuildingSuggestions?.storyEndConcept || ''
                            }}
                        />
                    ) : <PlaceholderMessage viewName="World Building" />
                )}

                {mainView === 'characters' && (
                    isInitialBlueprintReady ? (
                        <CharactersView />
                    ) : <PlaceholderMessage viewName="Characters" />
                )}

                {mainView === 'aiTools' && (
                    isInitialBlueprintReady ? (
                        <AIToolsView 
                            projectId={projectState.currentProjectId || 0}
                            projectState={projectState}
                            onProjectStateChange={handleProjectStateFieldChange as any}
                        />
                    ) : <PlaceholderMessage viewName="AI Writing Tools" />
                )}
                
                {mainView === 'bookStructureStudio' && (
                    isInitialBlueprintReady ? (
                         <BookStructureStudioView
                        projectState={projectState}
                        onFrontMatterChange={handleFrontMatterChange}
                        onBackMatterChange={handleBackMatterChange}
                        onAuthorDetailsChange={handleAuthorDetailsChange as any}
                        onAddBackMatterListItem={onAddBackMatterListItem}
                        onDeleteBackMatterListItem={onDeleteBackMatterListItem}
                        onUpdateBackMatterListItem={onUpdateBackMatterListItem}
                    />
                    ) : <PlaceholderMessage viewName="Book Structure Studio" />
                )}

                {mainView === 'chaptersBuilding' && ( 
                    <div className="flex flex-col h-full">
                        {!isInitialBlueprintReady && (
                             <PlaceholderMessage 
                                viewName="Chapters Building" 
                                customMessage='Please generate your Initial Blueprint (Book Details & World Building) from the "Project Idea" tab before building chapters.' 
                            />
                        )}
                        {isInitialBlueprintReady && (projectState as any)._hydrated && !projectState.chapters?.length && (
                            <div className="flex-grow flex flex-col items-center justify-center p-4 sm:p-8 bg-white rounded-xl shadow-lg">
                                <div className="text-center max-w-lg">
                                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                                        <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                                    </div>
                                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Ready to Build Your Chapters?</h2>
                                    <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 leading-relaxed">
                                        Your Book Details and World Building foundation is complete.
                                        Generate chapter outlines with detailed vitals to start writing your story.
                                    </p>
                                    <button
                                        onClick={projectSetupLogic.handleGenerateChapterOutlines}
                                        disabled={isLoading['chaptersGen']}
                                        className={`${primaryButtonClass} py-3 px-6 sm:py-4 sm:px-8 text-base sm:text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 w-full sm:w-auto`}
                                    >
                                        {isLoading['chaptersGen'] ? 'Generating Outlines & Vitals...' : 'Generate Chapter Outlines & Vitals'}
                                    </button>
                                </div>
                            </div>
                        )}
                        {isInitialBlueprintReady && projectState.chapters.length > 0 && (
                            <div className="flex flex-col h-full">
                                {showRevisionNeededBanner && (
                                    <div className="p-3 sm:p-4 mb-4 sm:mb-6 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl shadow-sm" role="alert">
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                                                    <span className="text-white text-xs font-bold">!</span>
                                                </div>
                                                <span className="font-medium">Content Update Required</span>
                                            </div>
                                            <button
                                                onClick={projectSetupLogic.handleGenerateChapterOutlines}
                                                disabled={isLoading['chaptersGen']}
                                                className="px-3 py-2 sm:px-4 sm:py-2 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors focus:ring-4 focus:ring-amber-300 w-full sm:w-auto"
                                            >
                                                {isLoading['chaptersGen'] ? 'Updating...' : 'Update Outlines'}
                                            </button>
                                        </div>
                                        <p className="text-amber-700 mt-2 text-xs">
                                            Book Details or World Building data has changed since chapter outlines were generated.
                                        </p>
                                    </div>
                                )}

                                {/* ✅ FIX: Always show regeneration option for existing chapters */}
                                {!showRevisionNeededBanner && (
                                    <div className="p-3 sm:p-4 mb-4 sm:mb-6 bg-blue-50 border border-blue-200 rounded-xl shadow-sm">
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                                    <span className="text-white text-xs font-bold">📝</span>
                                                </div>
                                                <span className="font-medium text-blue-800">Chapter Outlines Ready</span>
                                            </div>
                                            <button
                                                onClick={projectSetupLogic.handleGenerateChapterOutlines}
                                                disabled={isLoading['chaptersGen']}
                                                className="px-3 py-2 sm:px-4 sm:py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors focus:ring-4 focus:ring-blue-300 w-full sm:w-auto"
                                            >
                                                {isLoading['chaptersGen'] ? 'Regenerating...' : 'Regenerate Outlines'}
                                            </button>
                                        </div>
                                        <p className="text-blue-700 mt-2 text-xs">
                                            You can regenerate chapter outlines to refresh them with current story data.
                                        </p>
                                    </div>
                                )}
                                
                                {/* Modern Single-Scroll Layout */}
                                <div className="flex-1 flex flex-col lg:flex-row gap-6 min-h-0">
                                    {/* Chapter List Panel */}
                                    <div className="lg:w-80 xl:w-96 flex-shrink-0">
                                        <div className="bg-white rounded-xl shadow-lg border border-gray-200 h-full flex flex-col">
                                            <div className="px-6 py-4 border-b border-gray-200">
                                                <div className="flex items-center justify-between">
                                                    <h2 className="text-lg font-semibold text-gray-900">Chapters</h2>
                                                    <span className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full">
                                                        {projectState.chapters.length}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex-1 min-h-0">
                                                <div className="p-4 h-full">
                                                    <ChapterListPanel
                                                        projectState={projectState}
                                                        onSelectChapter={(chapterId: string) => {
                                                            handleSelectChapter(chapterId);
                                                        }}
                                                        onOpenSplitChapterModal={modalStates.openSplitChapterModal}
                                                        isLoading={isLoading}
                                                        selectedChapterIdsForMerge={projectState.selectedChapterIdsForMerge}
                                                        onToggleChapterForMerge={chapterManagement.handleToggleChapterForMerge}
                                                        isChapterSelectionDisabledForMerge={chapterManagement.isChapterSelectionDisabledForMerge}
                                                        canMergeSelectedChapters={chapterManagement.getMergeableSequenceInfo().isMergeable && chapterManagement.getMergeableSequenceInfo().selectedChapters.length >= 2}
                                                        onOpenMergeModal={chapterManagement.handleOpenMergeModal}
                                                        isInitialBlueprintReady={isInitialBlueprintReady}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Chapter Details Panel */}

                                        <div className="bg-white rounded-xl shadow-lg border border-gray-200 h-full flex flex-col">
                                            {activeChapter ? (
                                                <>
                                                    <div className="px-6 py-4 border-b border-gray-200">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <h2 className="text-lg font-semibold text-gray-900">
                                                                    Chapter {(activeChapter as any).chapterNumber || 'Details'}
                                                                </h2>
                                                                <p className="text-sm text-gray-600">{activeChapter.title || 'Untitled Chapter'}</p>
                                                            </div>
                                                            <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                                                                (activeChapter.status as string) === 'completed' ? 'bg-green-100 text-green-800' :
                                                                (activeChapter.status as string) === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                                                'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {(activeChapter.status as string) === 'completed' ? 'Completed' :
                                                                (activeChapter.status as string) === 'in_progress' ? 'In Progress' :
                                                                'Draft'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 min-h-0">

                                                            <ChapterDetailTabs
                                                                projectState={projectState}
                                                                activeChapter={activeChapter}
                                                                activeChapterDetailEdits={chapterState.activeChapterDetailEdits}
                                                                editor={editor}
                                                                revisionInput={revisionInput}
                                                                revisionWordCount={revisionWordCount}
                                                                isLoading={isLoading}
                                                                onDetailInputChange={chapterState.handleDetailInputChange}
                                                                onVitalsInputChange={chapterState.handleVitalsInputChange}
                                                                onSaveChapterDetails={chapterManagement.handleSaveChapterDetails}
                                                                onGenerateChapterPreview={chapterAIActions.handleGenerateChapterPreview}
                                                                onGenerateFullTextFromPreview={chapterAIActions.handleGenerateFullTextFromPreview}
                                                                onGenerateFullTextForAllPreviewedChapters={chapterAIActions.handleGenerateFullTextForAllPreviewedChapters}
                                                                onGenerateShortPreviewForAllChapters={chapterAIActions.handleGenerateShortPreviewForAllChapters}
                                                                onRevisionInputChange={setRevisionInput}
                                                                onRevisionWordCountChange={setRevisionWordCount}
                                                                onReviseText={chapterAIActions.handleReviseText}
                                                                onNavigateVersion={chapterManagement.navigateVersion}
                                                                onSetChapterStatus={chapterManagement.setChapterStatus}
                                                                areDetailsUnsaved={chapterState.areChapterDetailsCurrentlyUnsaved()}
                                                                chapterPreviewPercentage={chapterPreviewPercentage}
                                                                onChapterPreviewPercentageChange={onChapterPreviewPercentageChange}
                                                            />

                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex-1 flex items-center justify-center p-8">
                                                    <div className="text-center">
                                                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                                            <BookOpen className="w-8 h-8 text-gray-400" />
                                                        </div>
                                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Chapter Selected</h3>
                                                        <p className="text-gray-600 max-w-sm">
                                                            Select a chapter from the list to view its details and start writing.
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                </div>
                            </div>
                        )}
                    </div>
                )}

                {mainView === 'publishingEssentials' && (
                     isInitialBlueprintReady ? (
                        <div className="w-full bg-white p-6 rounded-lg shadow-xl flex-grow">
                            <PublishingEssentialsEditor
                                publishingEssentials={projectState.publishingEssentials}
                                onPublishingEssentialsChange={handlePublishingEssentialsChange}
                            />
                        </div>
                    ) : <PlaceholderMessage viewName="Publishing Essentials" />
                )}

                {mainView === 'bookPreview' && (
                    isInitialBlueprintReady ? (
                        <BookPreview projectState={projectState} />
                    ) : <PlaceholderMessage viewName="Book Preview" />
                )}
            </main>
        </div>
    );
};

export default MainWorkspace;